import { BackupOutlined, CloseOutlined } from "@material-ui/icons";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import axios from "axios";
import {
  AttachmentFilesCard,
  AutoCompleteComponent,
  AutoCompleteWithSearch,
  CurrencyNumberInput,
  FormLabel,
  ModalError,
  PrimaryButton,
  SecondaryButton,
  SelectInput,
} from "components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatFileSize, getErrors, stringTruncate } from "utils";

const ImportDocumentModalCWR = ({
  modalTitle,
  open,
  onClose,
  onSubmit,
  exchangeRate,
  loadingButton,
  fileType,
  multiple,
  downloadFile,
  onChangePublisher,
  onChangeFormat,
  roleType,
  preload,
  maxWidth,
  maxTruncate,
  downloadComposerTemplate,
  optionVersion,
}) => {
  const classes = globalStyles();
  const dialogRef = useRef();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [payload, setPayload] = useState(0);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [downloadedFile, setDownloadedFile] = useState("");

  const onDrop = useCallback(acceptedFiles => {
    multiple
      ? setDocumentFiles(prev => [...prev, ...acceptedFiles])
      : setDocumentFiles(acceptedFiles);
  }, []);
  const handleRemoveFile = idx => {
    setDocumentFiles(prevFiles => prevFiles.filter((_, i) => i !== idx));
  };

  const handleSubmit = () => {
    onSubmit(documentFiles, payload);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: fileType,
    multiple: multiple,
  });
  const downloadTemplate = async () => {
    try {
      setLoadingDownload(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/${
          downloadComposerTemplate
            ? "composer/export"
            : "song/download-template-catalogue"
        }`,
        { responseType: downloadComposerTemplate ? "blob" : "json", headers }
      );
      if (downloadComposerTemplate) {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "template.xlsx");
        document.body.appendChild(link);
        link.click();
      } else {
        setDownloadedFile(res?.data?.data);
      }
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingDownload(false);
    }
  };

  const dropzoneStyle = useMemo(() => {
    return isDragActive ? classes?.dragActive : "";
  }, [isDragActive, classes.activeStyle]);

  useEffect(() => {
    downloadFile && !downloadComposerTemplate && downloadTemplate();
  }, []);
  useEffect(() => {
    if (preload) {
      setDocumentFiles(preload);
    }
  }, [preload]);
  return (
    <Dialog
      ref={dialogRef}
      open={open}
      onClose={!loadingButton && onClose}
      fullWidth
      maxWidth={maxWidth || "xs"}
    >
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography fontWeight={700}>
              {modalTitle || "Import Document"}
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={!loadingButton && onClose}>
              <CloseOutlined />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <FormLabel label="Upload Document" />
        <div
          {...getRootProps({
            className: `${classes.dragFilesContainer} ${dropzoneStyle}`,
          })}
        >
          <input {...getInputProps()} />
          <Box color="grey">
            <BackupOutlined className={classes?.uploadIcon} />
          </Box>
          <Typography>
            {isDragActive
              ? "Drop the files here..."
              : "Drag files or click to upload"}
          </Typography>
        </div>
        <Grid container direction="column" my="16px" rowSpacing={2}>
          <Grid item>
            <AutoCompleteComponent
              label="Version"
              options={optionVersion}
              value={
                optionVersion.find(option => option.id === payload?.version) ||
                null
              }
              onChange={e => setPayload({ version: e })}
              size="small"
            />
          </Grid>
        </Grid>
        <Box mt="16px">
          <FormLabel label="Uploaded File" />
          <Grid container direction="column" rowSpacing={2} mb="16px">
            {documentFiles.length > 0 &&
              documentFiles?.map(({ name, size, url }, index) => (
                <Grid item key={index}>
                  <AttachmentFilesCard
                    fileName={name && stringTruncate(name, maxTruncate || 20)}
                    fileSize={size && formatFileSize(size)}
                    filePath={url}
                    handleRemove={() => handleRemoveFile(index)}
                  />
                </Grid>
              ))}
          </Grid>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <SecondaryButton
          label="Cancel"
          onClick={onClose}
          disabled={loadingButton}
        />
        <PrimaryButton
          label={loadingButton ? "Uploading" : "Upload"}
          disabled={loadingButton}
          loading={loadingButton}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};
const optionSociety = [
  {
    id: "asaba",
    name: "ASABA",
  },
  {
    id: "wami",
    name: "WAMI",
  },
];

export default ImportDocumentModalCWR;
